// frontend/src/components/AdventureComparisonCard.tsx
import React from 'react';
import { Adventure } from '../types/adventure';
import { t } from '../contexts/ThemeContext';

interface Props {
	adventure: Adventure;
	index: number;
	expanded: boolean;
	onToggle: () => void;
	isDark: boolean;
}

const AdventureComparisonCard: React.FC<Props> = ({ adventure, index, expanded, onToggle, isDark }) => {
	const tk = t(isDark);
	const borderColor = expanded
		? tk.textAccent
		: (isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0');
	const stopNames = (adventure.steps || []).map(s => s.activity).filter(Boolean);

	return (
		<button
			onClick={onToggle}
			style={{
				display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
				overflow: 'hidden', textAlign: 'left',
				backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'white',
				border: `2px solid ${borderColor}`,
				borderRadius: 16, padding: 20,
				cursor: 'pointer', fontFamily: 'inherit',
				boxShadow: expanded
					? (isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 4px 16px rgba(0,0,0,0.1)')
					: (isDark ? '0 2px 12px rgba(0,0,0,0.2)' : '0 1px 4px rgba(0,0,0,0.05)'),
				backdropFilter: 'blur(12px)',
				transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
				animation: 'cardPopIn 0.45s cubic-bezier(0.4,0,0.2,1) both',
				animationDelay: `${index * 90}ms`,
			}}
			onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
			onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
		>
			<h3 style={{ color: tk.textPrimary, margin: '0 0 6px 0', fontSize: '1.15rem' }}>
				{adventure.title}
			</h3>
			<p style={{ color: tk.textMuted, margin: '0 0 14px 0', fontStyle: 'italic', fontSize: '0.85rem' }}>
				{adventure.tagline}
			</p>

			<div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
				<span style={{ fontSize: '0.82rem', fontWeight: 600, color: tk.successDark }}>
					⏱️ {adventure.duration}min
				</span>
				<span style={{ fontSize: '0.82rem', fontWeight: 600, color: tk.errorDark }}>
					💰 ${adventure.cost}
				</span>
				<span style={{ fontSize: '0.82rem', fontWeight: 600, color: tk.textAccent }}>
					📍 {stopNames.length} stops
				</span>
			</div>

			{stopNames.length > 0 && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
					{stopNames.map((name, i) => (
						<div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: '0.82rem', color: tk.textSecondary, minWidth: 0 }}>
							<span style={{ color: tk.textMuted, flexShrink: 0 }}>{i + 1}.</span>
							<span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
						</div>
					))}
				</div>
			)}

			<div style={{
				fontSize: '0.82rem', fontWeight: 600,
				color: expanded ? tk.textAccent : tk.textMuted,
				display: 'flex', alignItems: 'center', gap: 6,
			}}>
				{expanded ? '▼ Viewing details' : '▶ View full details'}
			</div>
		</button>
	);
};

export default AdventureComparisonCard;
